import { Fragment, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { cn } from './cn'
import { IconChevronDown, IconChevronExpand, IconChevronRight, IconChevronUp } from './Icons'

export interface TableColumn<RowT = Record<string, unknown>> {
  key: string
  label: ReactNode
  className?: string
  cellClassName?: string
  sortable?: boolean
  sortDirection?: 'asc' | 'desc' | null
  onSort?: () => void
  render?: (row: RowT) => ReactNode
  /**
   * 1 = primary column (always visible). Values &gt; 1 collapse behind the mobile
   * row expander below the `md` breakpoint (same contract as DataTableV2).
   * Omit `priority` on all columns to keep legacy auto-width + horizontal scroll.
   */
  priority?: number
}

export interface TableProps<RowT = Record<string, unknown>> {
  columns: TableColumn<RowT>[]
  data: RowT[]
  onRowClick?: (row: RowT) => void
  getRowKey?: (row: RowT, index: number) => string | number
  emptyMessage?: ReactNode
  className?: string
  ariaLabel?: string
  rowClassName?: (row: RowT, index: number) => string
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function defaultRowKey<RowT>(row: RowT, index: number): string | number {
  if (isPlainRecord(row) && 'id' in row) {
    const id = row.id
    if (typeof id === 'string' || typeof id === 'number') return id
  }
  return JSON.stringify(row) || `row-${index}`
}

function defaultCellValue<RowT>(row: RowT, key: string): ReactNode {
  return isPlainRecord(row) ? row[key] as ReactNode : undefined
}

function hasMobilePriorityLayout<RowT>(columns: TableColumn<RowT>[]): boolean {
  return columns.some((column) => column.priority !== undefined)
}

function priorityClass<RowT>(column: TableColumn<RowT>, mobilePriority: boolean): string {
  if (!mobilePriority) return ''
  return (column.priority ?? 1) > 1 ? 'hidden md:table-cell' : ''
}

function rowSpan<RowT>(columns: TableColumn<RowT>[], hiddenCount: number): number {
  return columns.length + (hiddenCount > 0 ? 1 : 0)
}

function toggleExpanded(current: Set<string>, key: string): Set<string> {
  const next = new Set(current)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

function SortableHeader<RowT>({ col }: { col: TableColumn<RowT> }) {
  if (col.sortable && col.onSort) {
    return (
      <button
        type="button"
        onClick={col.onSort}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          col.onSort?.()
        }}
        className="inline-flex w-full min-w-0 items-center gap-1 cursor-pointer select-none text-left font-semibold uppercase tracking-wider text-xs text-inherit focus-visible:outline-none focus-visible:shadow-focus"
      >
        {col.label}
        {col.sortDirection === 'asc' ? <IconChevronUp size={14} /> : col.sortDirection === 'desc' ? <IconChevronDown size={14} /> : <IconChevronExpand size={14} className="opacity-40" />}
      </button>
    )
  }
  return <>{col.label}</>
}

export function Table<RowT>({ columns, data, onRowClick, getRowKey, emptyMessage = 'No data found.', className = '', ariaLabel = 'Data table', rowClassName }: TableProps<RowT>) {
  const mobilePriority = hasMobilePriorityLayout(columns)

  if (!mobilePriority) {
    return (
      <div className={`overflow-x-auto ${className}`}>
        <table className="w-full text-sm" aria-label={ariaLabel}>
          <thead>
            <tr className="border-b border-line">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={
                    col.sortable && col.onSort
                      ? col.sortDirection
                        ? col.sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                      : undefined
                  }
                  className={`px-4 py-3 text-left font-semibold text-ink-muted uppercase tracking-wider text-xs ${col.className || ''}`}
                >
                  <SortableHeader col={col} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-ink-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr
                  key={String(getRowKey ? getRowKey(row, i) : defaultRowKey(row, i))}
                  onClick={() => onRowClick?.(row)}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={(event) => {
                    if (!onRowClick) return
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    onRowClick(row)
                  }}
                  className={`${onRowClick ? 'cursor-pointer hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus' : ''} transition-colors duration-fast ease-out-soft ${rowClassName ? rowClassName(row, i) : ''}`}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-3 text-ink-body ${col.cellClassName || ''}`}>
                      {col.render ? col.render(row) : defaultCellValue(row, col.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <TableWithMobilePriority
      columns={columns}
      data={data}
      onRowClick={onRowClick}
      getRowKey={getRowKey}
      emptyMessage={emptyMessage}
      className={className}
      ariaLabel={ariaLabel}
      rowClassName={rowClassName}
    />
  )
}

function TableWithMobilePriority<RowT>({ columns, data, onRowClick, getRowKey, emptyMessage, className, ariaLabel, rowClassName }: TableProps<RowT>) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const hiddenColumns = columns.filter((column) => (column.priority ?? 1) > 1)
  const resolveKey = (row: RowT, index: number) => String(getRowKey ? getRowKey(row, index) : defaultRowKey(row, index))

  return (
    <div
      className={cn(
        'min-w-0 max-w-full overflow-x-auto shadow-scroll-hint',
        className,
      )}
    >
      <table className="w-full min-w-0 table-fixed text-sm md:table-auto" aria-label={ariaLabel}>
        <thead>
          <tr className="border-b border-line">
            {hiddenColumns.length > 0 && (
              <th scope="col" className="w-11 px-3 md:hidden">
                <span className="sr-only">Details</span>
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                aria-sort={
                  col.sortable && col.onSort
                    ? col.sortDirection
                      ? col.sortDirection === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                    : undefined
                }
                className={cn(
                  'px-4 py-3 text-left font-semibold text-ink-muted uppercase tracking-wider text-xs max-md:min-w-0',
                  priorityClass(col, true),
                  col.className || '',
                )}
              >
                <SortableHeader col={col} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {data.length === 0 ? (
            <tr>
              <td colSpan={rowSpan(columns, hiddenColumns.length)} className="px-4 py-8 text-center text-ink-muted">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, i) => {
              const rowKey = resolveKey(row, i)
              const isExpanded = expanded.has(rowKey)
              const rowClickProps = onRowClick
                ? {
                    onClick: () => onRowClick(row),
                    role: 'button' as const,
                    tabIndex: 0,
                    onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onRowClick(row)
                    },
                    className: 'cursor-pointer hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus',
                  }
                : {}

              return (
                <Fragment key={rowKey}>
                  <tr
                    {...rowClickProps}
                    className={cn('transition-colors duration-fast ease-out-soft', rowClickProps.className, rowClassName ? rowClassName(row, i) : '')}
                  >
                    {hiddenColumns.length > 0 && (
                      <td className="px-3 align-middle md:hidden">
                        <button
                          type="button"
                          aria-label="Expand row details"
                          aria-expanded={isExpanded}
                          onClick={(event) => {
                            event.stopPropagation()
                            setExpanded((current) => toggleExpanded(current, rowKey))
                          }}
                          className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-control text-ink-muted hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus"
                        >
                          <IconChevronRight size={16} className={cn('transition-transform duration-fast ease-out-soft', isExpanded && 'rotate-90')} />
                        </button>
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'px-4 py-3 text-ink-body max-md:min-w-0 max-md:overflow-hidden',
                          priorityClass(col, true),
                          col.cellClassName || '',
                        )}
                      >
                        {col.render ? col.render(row) : defaultCellValue(row, col.key)}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && hiddenColumns.length > 0 && (
                    <tr key={`${rowKey}-details`} className="border-b border-line bg-whisper md:hidden">
                      <td colSpan={rowSpan(columns, hiddenColumns.length)} className="px-4 py-3">
                        <dl className="grid gap-2 text-sm">
                          {hiddenColumns.map((col) => (
                            <div key={col.key} className="grid grid-cols-[minmax(6rem,8rem)_minmax(0,1fr)] gap-3">
                              <dt className="text-ink-muted">{col.label}</dt>
                              <dd className="m-0 min-w-0 break-words text-ink-body">
                                {col.render ? col.render(row) : defaultCellValue(row, col.key)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
